<template>
  <b-card-code title="Tankolás rögzítés">
    <b-alert
      show
      variant="info"
      class="mb-2"
    >
      A dátum/idő automatikus. A tankolás a bejelentkezett felhasználó nevében kerül mentésre.
    </b-alert>

    <b-alert
      show
      variant="secondary"
      class="mb-2"
    >
      <div class="font-weight-bold mb-1">
        Aktuális készlet a tartályban:
      </div>
      <ul class="mb-0 pl-2">
        <li v-for="item in tankStocks" :key="item.company">
          {{ item.company }}: {{ formatLiter(item.liter) }} liter
        </li>
      </ul>
    </b-alert>

    <b-form @submit.prevent="submitForm">
      <b-row>
        <b-col
          v-if="canManageCorrectionFactor"
          cols="12"
        >
          <b-form-group label="Korrekciós mértékadó *">
            <b-form-input
              v-model="form.correction_factor"
              type="number"
              step="0.0001"
              min="0"
              placeholder="pl. 0.95"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Gép neve *">
            <b-form-select
              v-model="form.machine_id"
              :options="machineOptions"
              value-field="value"
              text-field="text"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Gépkezelő neve *">
            <b-form-select
              v-model="form.operator_id"
              :options="operatorOptions"
              value-field="value"
              text-field="text"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Tankolt mennyiség(liter) *">
            <b-form-input
              v-model="form.liter_muszer"
              type="number"
              step="0.001"
              min="0"
              placeholder="pl. 125.500"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Üzemóra állás *">
            <b-form-input
              v-model="form.uzemora"
              type="number"
              step="0.01"
              min="0"
              placeholder="pl. 4521.30"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Aláírás *">
        <small class="text-muted d-block mb-1">
          {{ hasSignature ? 'Aláírás rögzítve' : 'Aláírás még nincs rögzítve' }}
        </small>
        <div class="border rounded">
          <canvas
            ref="signatureCanvas"
            class="w-100 rounded"
            :height="signatureCanvasHeight"
            @mousedown="startDraw"
            @mousemove="draw"
            @mouseup="endDraw"
            @mouseleave="endDraw"
            @touchstart.prevent="startDrawTouch"
            @touchmove.prevent="drawTouch"
            @touchend.prevent="endDraw"
          />
        </div>
        <div class="d-flex mt-1">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="clearSignature"
          >
            Aláírás törlése
          </b-button>
        </div>

        <div class="pt-2">
          <b-button
            variant="primary"
            type="submit"
            block
            :disabled="isSaving"
          >
            {{ isSaving ? 'Mentés folyamatban...' : 'Mentés' }}
          </b-button>
        </div>
      </b-form-group>
    </b-form>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { checkRole } from '@/middleware/authLoader'
import axios from 'axios'
import {
  BAlert,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
} from 'bootstrap-vue'

export default {
  name: 'TankolasRecord',
  components: {
    BCardCode,
    BAlert,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
  },
  data() {
    return {
      isSaving: false,
      isDrawing: false,
      hasSignature: false,
      signatureCanvasHeight: 180,
      form: {
        correction_factor: null,
        machine_id: null,
        operator_id: null,
        liter_muszer: null,
        uzemora: null,
      },
      machineOptions: [
        { value: null, text: 'Válassz gépet' },
      ],
      operatorOptions: [
        { value: null, text: 'Válassz gépkezelőt' },
      ],
      tankStocks: [
        { company: 'Monolit-Profi Kft', liter: 0 },
        { company: 'Monolit-Épszer Kft', liter: 0 },
      ],
      canManageCorrectionFactor: false,
    }
  },
  computed: {
    isTankolasAdmin() {
      return checkRole('tankolas_role_admin')
    },
  },
  mounted() {
    this.setupCanvas()
    this.loadOptions()
    window.addEventListener('resize', this.setupCanvas)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setupCanvas)
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    toastError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Mentés sikertelen.',
          icon: 'XOctagonIcon',
          variant: 'danger',
        },
      })
    },
    toastSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Sikeres mentés.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
    async loadOptions() {
      try {
        const response = await axios({
          method: 'get',
          url: 'tankolas/record/options',
          headers: this.authHeaders(),
        })

        const machines = Array.isArray(response.data.machines) ? response.data.machines : []
        const operators = Array.isArray(response.data.operators) ? response.data.operators : []
        const tankStocks = Array.isArray(response?.data?.tank_stocks) ? response.data.tank_stocks : []
        const correctionFactor = Number(response?.data?.correction_factor)
        this.canManageCorrectionFactor = Boolean(response?.data?.can_manage_correction_factor) && this.isTankolasAdmin

        this.machineOptions = [
          { value: null, text: 'Válassz gépet' },
          ...machines.map(item => ({
            value: item.id,
            text: item.name,
          })),
        ]

        this.operatorOptions = [
          { value: null, text: 'Válassz gépkezelőt' },
          ...operators.map(item => ({
            value: item.id,
            text: item.name,
          })),
        ]
        this.tankStocks = this.normalizeTankStocks(tankStocks)

        if (Number.isFinite(correctionFactor) && correctionFactor > 0) {
          this.form.correction_factor = correctionFactor
        } else if (this.form.correction_factor === null) {
          this.form.correction_factor = 0.95
        }
      } catch (error) {
        this.toastError('A gép és gépkezelő lista nem tölthető be.')
      }
    },
    normalizeTankStocks(items) {
      const defaults = {
        'Monolit-Profi Kft': 0,
        'Monolit-Épszer Kft': 0,
      }
      items.forEach(item => {
        const company = item?.company
        const liter = Number(item?.liter)
        if (Object.prototype.hasOwnProperty.call(defaults, company)) {
          defaults[company] = Number.isFinite(liter) && liter > 0 ? liter : 0
        }
      })
      return Object.keys(defaults).map(company => ({
        company,
        liter: defaults[company],
      }))
    },
    formatLiter(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '0'
      return number.toLocaleString('hu-HU', { maximumFractionDigits: 3 })
    },
    getSignatureStrokeColor() {
      const canvas = this.$refs.signatureCanvas
      if (!canvas) return '#ffffff'
      const bg = window.getComputedStyle(canvas).backgroundColor
      const matches = bg && bg.match(/\d+/g)
      if (!matches || matches.length < 3) return '#ffffff'
      const r = Number(matches[0])
      const g = Number(matches[1])
      const b = Number(matches[2])
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5 ? '#1f2b4d' : '#ffffff'
    },
    setupCanvas() {
      const canvas = this.$refs.signatureCanvas
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      canvas.width = canvas.clientWidth
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = this.getSignatureStrokeColor()
      if (!this.hasSignature) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    },
    getCanvasPoint(event) {
      const canvas = this.$refs.signatureCanvas
      const rect = canvas.getBoundingClientRect()
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
    },
    startDraw(event) {
      const canvas = this.$refs.signatureCanvas
      const ctx = canvas.getContext('2d')
      const point = this.getCanvasPoint(event)
      this.isDrawing = true
      ctx.beginPath()
      ctx.moveTo(point.x, point.y)
    },
    draw(event) {
      if (!this.isDrawing) return
      const canvas = this.$refs.signatureCanvas
      const ctx = canvas.getContext('2d')
      const point = this.getCanvasPoint(event)
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
      this.hasSignature = true
    },
    endDraw() {
      this.isDrawing = false
    },
    startDrawTouch(event) {
      if (!event.touches || !event.touches[0]) return
      this.startDraw(event.touches[0])
    },
    drawTouch(event) {
      if (!event.touches || !event.touches[0]) return
      this.draw(event.touches[0])
    },
    clearSignature() {
      const canvas = this.$refs.signatureCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      this.hasSignature = false
    },
    validateForm() {
      if (!this.form.machine_id) return 'A gép kiválasztása kötelező.'
      if (!this.form.operator_id) return 'A gépkezelő kiválasztása kötelező.'
      if (this.canManageCorrectionFactor && (this.form.correction_factor === null || this.form.correction_factor === '')) {
        return 'A korrekciós mértékadó kötelező.'
      }
      if (this.form.liter_muszer === null || this.form.liter_muszer === '') return 'A műszer szerinti liter kötelező.'
      if (this.form.uzemora === null || this.form.uzemora === '') return 'Az üzemóra kötelező.'
      if (!this.hasSignature) {
        return 'Az aláírás kötelező.'
      }
      return null
    },
    extractBackendError(error) {
      if (!error || !error.response || !error.response.data) return 'Mentés sikertelen.'
      const data = error.response.data
      if (data.message) return data.message
      if (data.errors && typeof data.errors === 'object') {
        const firstField = Object.keys(data.errors)[0]
        if (firstField && Array.isArray(data.errors[firstField]) && data.errors[firstField][0]) {
          return data.errors[firstField][0]
        }
      }
      return 'Mentés sikertelen.'
    },
    async submitForm() {
      const validationError = this.validateForm()
      if (validationError) {
        this.toastError(validationError)
        return
      }

      const canvas = this.$refs.signatureCanvas
      const signature = canvas.toDataURL('image/png')

      this.isSaving = true
      try {
        const response = await axios({
          method: 'post',
          url: 'tankolas/record',
          headers: this.authHeaders(),
          data: {
            correction_factor: this.canManageCorrectionFactor ? this.form.correction_factor : null,
            machine_id: this.form.machine_id,
            operator_id: this.form.operator_id,
            liter_muszer: this.form.liter_muszer,
            uzemora: this.form.uzemora,
            signature,
          },
        })

        this.toastSuccess(response.data.message || 'Tankolás sikeresen mentve.')

        this.form.liter_muszer = null
        this.form.uzemora = null
        this.clearSignature()
        await this.loadOptions()
      } catch (error) {
        this.toastError(this.extractBackendError(error))
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>
